import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { syncEngine, type SyncStatus } from '@/lib/sync';
import { getTotalPendingCount } from '@/lib/offline';

/* ==========================================================================
   OnlineContext — provides connectivity status & sync control to the app.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface OnlineContextValue {
  /** Whether the browser reports being online. */
  isOnline: boolean;
  /** Current sync engine status. */
  syncStatus: SyncStatus;
  /** Number of items waiting to be synced. */
  pendingCount: number;
  /** Timestamp of last successful sync. */
  lastSyncAt: Date | null;
  /** Manually trigger a full sync cycle. */
  forceSync: () => Promise<void>;
}

/* --------------------------------------------------------------------------
   Context
   -------------------------------------------------------------------------- */

export const OnlineContext = createContext<OnlineContextValue | null>(null);

/* --------------------------------------------------------------------------
   Provider
   -------------------------------------------------------------------------- */

export function OnlineProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(
    navigator.onLine ? 'idle' : 'offline',
  );
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);

  // ------------------------------------------------------------------
  // Online/offline events
  // ------------------------------------------------------------------

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // ------------------------------------------------------------------
  // Sync engine integration
  // ------------------------------------------------------------------

  useEffect(() => {
    // Start the sync engine
    syncEngine.start();

    // Subscribe to status changes
    const unsubStatus = syncEngine.onStatusChange((status) => {
      setSyncStatus(status);
      if (status === 'idle') {
        setLastSyncAt(syncEngine.getLastSyncAt());
      }
    });

    // Subscribe to count changes
    const unsubCount = syncEngine.onCountChange((count) => {
      setPendingCount(count);
    });

    // Get initial pending count
    void getTotalPendingCount().then(setPendingCount).catch(() => {
      // IndexedDB might not be available
    });

    return () => {
      unsubStatus();
      unsubCount();
      syncEngine.stop();
    };
  }, []);

  // ------------------------------------------------------------------
  // Force sync
  // ------------------------------------------------------------------

  const forceSync = useCallback(async () => {
    await syncEngine.processAll();
    // Refresh pending count after forced sync
    try {
      const count = await getTotalPendingCount();
      setPendingCount(count);
    } catch {
      // IndexedDB error — not critical
    }
  }, []);

  // ------------------------------------------------------------------
  // Context value
  // ------------------------------------------------------------------

  const value = useMemo<OnlineContextValue>(
    () => ({
      isOnline,
      syncStatus,
      pendingCount,
      lastSyncAt,
      forceSync,
    }),
    [isOnline, syncStatus, pendingCount, lastSyncAt, forceSync],
  );

  return (
    <OnlineContext.Provider value={value}>{children}</OnlineContext.Provider>
  );
}
